import React, { useState } from "react";
import { useTransientMessage } from "hooks/useTransientMessage";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import Card from "components/card";
import { useUser, useUserOperations } from "../../../hooks/useUsers";
import { useUserDetailed } from "../../../hooks/useUserDetailed";
import { useImageUpload } from "../../../hooks/useImageUpload";
import { UserService } from "../../../services/userService";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import ChangePasswordModal from "../../../components/users/ChangePasswordModal";
import UserDetailHeader from "../../../components/users/UserDetailHeader";
import UserProfileCard from "../../../components/users/UserProfileCard";
import UserAccountInfoCard from "../../../components/users/UserAccountInfoCard";
import FitnessDataDisplay from "../../../components/users/FitnessDataDisplay";
import WeightProgressImagesGallery from "../../../components/users/WeightProgressImagesGallery";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import ImageErrorToast from "../../../components/common/ImageErrorToast";
import UserMeasurementLogsCard from "../../../components/users/UserMeasurementLogsCard";
import UserCheckInsCard from "../../../components/users/UserCheckInsCard";
import FitnessDataModal from "../../../components/users/FitnessDataModal";
import { UpdateFitnessDataInput } from "../../../types/fitness";
import { UpdateUserInput } from "../../../types/user";
import UserModal from "../../../components/users/UserModal";
import { useAuth } from "../../../contexts/AuthContext";
import UserFinancialInfo from "../../../components/users/UserFinancialInfo";
import UserTrialAccessCard from "../../../components/users/UserTrialAccessCard";
import UserChallengeAccessCard from "../../../components/users/UserChallengeAccessCard";
import GrantTrialModal from "../../../components/users/GrantTrialModal";
import {
  useUserFinancialInfo,
  useUserTrials,
  useTrialOperations,
} from "../../../hooks/useFinancial";
import { Trial, TrialEntitlement } from "../../../types/financial";

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: loggedInUser, refreshUser } = useAuth();

  // Parse and validate the user ID
  const userId = id && !isNaN(parseInt(id)) ? parseInt(id) : null;

  const { user, loading, error, refresh } = useUser(userId);
  const {
    updateUser,
    deleteUser: deleteUserOperation,
    loading: operationLoading,
    error: operationError,
    clearError: clearOperationError,
  } = useUserOperations();
  const {
    financialInfo,
    loading: financialLoading,
    error: financialError,
    refresh: refreshFinancial,
  } = useUserFinancialInfo(userId);

  // Trial access hooks
  const {
    activeTrial,
    trialHistory,
    loading: trialsLoading,
    error: trialsError,
    refresh: refreshTrials,
  } = useUserTrials(userId);
  const {
    loading: trialOperationLoading,
    error: trialOperationError,
    grantTrial,
    revokeTrial,
    clearError: clearTrialError,
  } = useTrialOperations();

  // Detailed user data with fitness_data and weight_progress_images
  const {
    detailedUser,
    loading: loadingDetailed,
    reload: reloadDetailed,
  } = useUserDetailed(userId);

  // Trial modal states
  const [showGrantTrialModal, setShowGrantTrialModal] = useState(false);
  const [showRevokeTrialModal, setShowRevokeTrialModal] = useState(false);
  const [trialToRevoke, setTrialToRevoke] = useState<Trial | null>(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(
    null
  );
  const [successMessage, setSuccessMessage] = useTransientMessage(5000);

  // Fitness data modal state
  const [showFitnessModal, setShowFitnessModal] = useState(false);
  const [savingFitnessData, setSavingFitnessData] = useState(false);
  const [fitnessError, setFitnessError] = useState<string>("");

  // Check if this is the logged-in user's profile
  const isMyProfile = loggedInUser && user && loggedInUser.id === user.id;

  // Same modal and payload as the users list, so the two edit paths cannot
  // drift.
  const handleOpenEditModal = () => {
    clearOperationError();
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    clearOperationError();
  };

  const handleEditSubmit = async (userData: any) => {
    if (!user) return;

    const updateData: UpdateUserInput = {
      first_name: userData.first_name,
      last_name: userData.last_name,
      gender: userData.gender,
      date_of_birth: userData.date_of_birth,
      height: userData.height,
    };

    const updatedUser = await updateUser(user.id, updateData);
    if (updatedUser) {
      refresh();
      setShowEditModal(false);
    }
  };

  const refreshAfterImageChange = async () => {
    refresh();
    if (isMyProfile) {
      await refreshUser();
    }
  };

  const image = useImageUpload({
    upload: user ? (file) => UserService.uploadProfileImage(file) : null,
    remove:
      user && user.profile_image?.id
        ? () => UserService.deleteProfileImage(user.id, user.profile_image.id)
        : null,
    hasImage: !!user?.profile_image,
    onUploaded: refreshAfterImageChange,
    onDeleted: refreshAfterImageChange,
    deleteErrorMessage: "Failed to delete profile image",
  });

  const handleBack = () => {
    const returnPath = "/admin/users" + ((location.state as any)?.from || "");
    navigate(returnPath);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleChangePassword = () => {
    setChangePasswordError(null);
    setShowChangePasswordModal(true);
  };

  const handleChangePasswordSubmit = async (
    userId: number,
    password: string,
    passwordConfirmation: string
  ) => {
    setChangePasswordLoading(true);
    setChangePasswordError(null);

    try {
      const result = await UserService.changeUserPassword(
        userId,
        password,
        passwordConfirmation
      );

      if (result.success) {
        setShowChangePasswordModal(false);
        setSuccessMessage("Password changed successfully");
      } else {
        setChangePasswordError(result.error || "Failed to change password");
      }
    } catch (error: any) {
      setChangePasswordError(error.message || "An unexpected error occurred");
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setChangePasswordError(null);
  };

  const confirmDeleteUser = async () => {
    if (!user) return;

    const success = await deleteUserOperation(user.id);
    if (success) {
      const returnPath = "/admin/users" + ((location.state as any)?.from || "");
      navigate(returnPath);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Fitness data handlers
  const handleOpenFitnessModal = () => {
    setFitnessError("");
    setShowFitnessModal(true);
  };

  const handleCloseFitnessModal = () => {
    setShowFitnessModal(false);
    setFitnessError("");
  };

  const handleSaveFitnessData = async (data: UpdateFitnessDataInput) => {
    if (!userId) return;

    setFitnessError("");
    setSavingFitnessData(true);
    try {
      const result = await UserService.updateUserFitnessData(userId, data);

      if (result.success) {
        await reloadDetailed();
        setShowFitnessModal(false);
        setSuccessMessage("Fitness data updated successfully");
      } else {
        setFitnessError(result.error || "Failed to update fitness data");
      }
    } catch (err: any) {
      setFitnessError(err.message || "Failed to update fitness data");
    } finally {
      setSavingFitnessData(false);
    }
  };

  // Trial access handlers
  const handleOpenGrantTrialModal = () => {
    clearTrialError();
    setShowGrantTrialModal(true);
  };

  const handleCloseGrantTrialModal = () => {
    setShowGrantTrialModal(false);
    clearTrialError();
  };

  const handleGrantTrialSubmit = async (
    trialDays: number,
    entitlements: TrialEntitlement[]
  ) => {
    if (!userId) return;

    const result = await grantTrial({
      user_id: userId,
      trial_days: trialDays,
      entitlements,
    });
    if (result) {
      refreshTrials();
      setShowGrantTrialModal(false);
      setSuccessMessage(`Trial access granted for ${trialDays} days`);
    }
  };

  const handleRevokeTrialClick = (trial: Trial) => {
    setTrialToRevoke(trial);
    setShowRevokeTrialModal(true);
  };

  const handleCloseRevokeTrialModal = () => {
    setShowRevokeTrialModal(false);
    setTrialToRevoke(null);
  };

  const handleConfirmRevokeTrial = async () => {
    if (!trialToRevoke) return;

    const success = await revokeTrial(trialToRevoke.id);
    if (success) {
      refreshTrials();
      setShowRevokeTrialModal(false);
      setTrialToRevoke(null);
      setSuccessMessage("Trial access revoked successfully");
    }
  };

  // Early returns live below every hook and handler so hook order is stable.
  if (!userId) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">Invalid user ID</div>
          <button
            onClick={() => navigate("/admin/users")}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Users</span>
          </button>
        </div>
      </Card>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !user) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">
            {error || "User not found"}
          </div>
          <button
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Users</span>
          </button>
        </div>
      </Card>
    );
  }

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const userLabel = fullName || user.email;
  // Null names render as-is; only an all-empty name falls back to the email.
  const deleteItemName =
    `${user.first_name} ${user.last_name}`.trim() || user.email;

  return (
    <div className="space-y-6">
      <UserDetailHeader
        isMyProfile={!!isMyProfile}
        onBack={handleBack}
        onEdit={handleOpenEditModal}
        onChangePassword={handleChangePassword}
        onDelete={handleDelete}
      />

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/30">
          <div className="text-green-800 dark:text-green-200">
            {successMessage}
          </div>
        </div>
      )}

      <UserProfileCard
        user={user}
        fullName={fullName}
        isMyProfile={!!isMyProfile}
        image={image}
      />

      <UserAccountInfoCard user={user} />

      {/* Financial Information Section */}
      <UserFinancialInfo
        financialInfo={financialInfo}
        loading={financialLoading}
        error={financialError}
        onRefresh={refreshFinancial}
      />

      {/* Trial Access Section */}
      <UserTrialAccessCard
        activeTrial={activeTrial}
        trialHistory={trialHistory}
        loading={trialsLoading}
        error={trialsError}
        hasActiveSubscription={financialInfo?.has_active_subscription ?? false}
        onGrantTrial={handleOpenGrantTrialModal}
        onRevokeTrial={handleRevokeTrialClick}
        onRefresh={refreshTrials}
      />

      {/* Challenge Access Section: only for users who have been in a challenge */}
      {!loadingDetailed && (
        <UserChallengeAccessCard
          challenges={detailedUser?.challenges}
          onOpenChallenge={(challengeId) =>
            navigate(`/admin/challenges/${challengeId}`)
          }
        />
      )}

      {/* Fitness Data Section */}
      {loadingDetailed ? (
        <Card extra="w-full p-3 md:p-6">
          <LoadingSpinner />
        </Card>
      ) : (
        <FitnessDataDisplay
          fitnessData={detailedUser?.fitness_data}
          onEdit={handleOpenFitnessModal}
        />
      )}

      {/* Weight Progress Images Section */}
      {loadingDetailed ? (
        <Card extra="w-full p-3 md:p-6">
          <LoadingSpinner />
        </Card>
      ) : (
        <WeightProgressImagesGallery
          images={detailedUser?.weight_progress_images}
        />
      )}

      <UserMeasurementLogsCard userId={userId} />

      <UserCheckInsCard userId={userId} />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        itemName={deleteItemName}
        loading={operationLoading}
        error={operationError}
      />

      {/* Delete Profile Image Modal */}
      <DeleteConfirmationModal
        isOpen={image.showDeleteModal}
        onClose={image.closeDeleteModal}
        onConfirm={image.confirmDelete}
        title="Delete Profile Image"
        message="Are you sure you want to delete this user's profile image? This action cannot be undone."
        itemName={deleteItemName}
        loading={image.deleting}
        error={image.error}
      />

      <ImageErrorToast message={image.error} onDismiss={image.clearError} />

      {/* Fitness Data Modal */}
      <FitnessDataModal
        isOpen={showFitnessModal}
        onClose={handleCloseFitnessModal}
        onSave={handleSaveFitnessData}
        fitnessData={detailedUser?.fitness_data}
        loading={savingFitnessData}
        error={fitnessError}
      />

      <UserModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleEditSubmit}
        user={user}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={handleCloseChangePasswordModal}
        onSubmit={handleChangePasswordSubmit}
        user={user}
        loading={changePasswordLoading}
        error={changePasswordError}
      />

      {/* Grant Trial Modal */}
      <GrantTrialModal
        isOpen={showGrantTrialModal}
        onClose={handleCloseGrantTrialModal}
        onSubmit={handleGrantTrialSubmit}
        userName={userLabel}
        loading={trialOperationLoading}
        error={trialOperationError}
      />

      {/* Revoke Trial Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showRevokeTrialModal}
        onClose={handleCloseRevokeTrialModal}
        onConfirm={handleConfirmRevokeTrial}
        title="Revoke Trial Access"
        message="Are you sure you want to revoke this trial access? The user will immediately lose their trial privileges."
        itemName={trialToRevoke ? `${trialToRevoke.trial_days} day trial` : ""}
        loading={trialOperationLoading}
        error={trialOperationError}
        confirmButtonText="Revoke"
        confirmButtonLoadingText="Revoking..."
      />
    </div>
  );
};

export default UserDetail;
